// Order sets (PROD-2850, v3 board 6 "ApAfter"): an education article IN ITS DIAGNOSIS CARD —
// "Living with type 2 diabetes · EDU · Article · sent after signing". Only an article a set
// attached renders here (`groupOrdersByDiagnosis().groups[].articles`); a hand-added one stays in
// the A/P's Education section.
//
// Board 6, measured: the order-row anatomy the Settings set builder draws for the same item (the
// grey-100 panel, the 2px brand bar inset 8px, the 18px brand glyph, the 15px title, the EDU tag in
// info tint and brand-depth ink, the 13px secondary detail line, the 16px close at the end). An
// article is not an order, so it has no order form to open and no diagnosis-link line; its only
// action is remove, the same remove the Education section offers.
import { X } from 'lucide-react';
import { frameV2Swap } from '@/shared/ui/design-v2';
import {
  formatItemDetails,
  itemTypeTag,
  orderSetTypeLucide,
} from '@/settings/components/order-sets/order-set-item-details';
import type { OrderSetItem } from '@/types/order-set-api';
import type { EducationArticle } from '../lib/to-education-article';

/** The tag and detail line are the Settings builder's own for an education item ("EDU", "Article · sent after signing"). */
const EDUCATION: Pick<OrderSetItem, 'type' | 'code' | 'details'> = {
  type: 'education',
  code: { system: '', code: '', display: '' },
  details: {},
};

/** The education item's glyph, the order-set type map's Lucide twin (icon pass, 16 Oct). */
const EducationGlyph = orderSetTypeLucide('education');

export interface ApEducationRowProps {
  article: EducationArticle;
  /** Remove the article (absent, or read-only → no control). */
  onRemove?: () => void;
  readOnly?: boolean;
}

export function ApEducationRow({ article, onRemove, readOnly }: ApEducationRowProps) {
  const name = article.display;
  return (
    <div
      role="group"
      aria-label={`Education: ${name}`}
      data-slot="ap-education-row"
      className="flex items-stretch gap-3 pl-2 rounded-md bg-grey-100"
    >
      <span
        aria-hidden="true"
        className={frameV2Swap('my-2 w-0.5 shrink-0 rounded-tag bg-brand-primary', 'bg-wayfind-medications-ink')}
      />
      <span className="flex items-start pt-3 text-text-secondary">
        <EducationGlyph className="size-4" aria-hidden />
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1 py-2">
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="text-size-body-15 leading-body text-text-primary">{name}</span>
          <span
            className={frameV2Swap(
              'px-1 py-0.5 rounded-tag bg-status-info-bg text-brand-depth text-size-2xs font-bold tracking-label',
              'bg-surface-card text-text-secondary',
            )}
          >
            {itemTypeTag(EDUCATION)}
          </span>
        </span>
        <span className="text-size-sm leading-body text-text-secondary">{formatItemDetails(EDUCATION)}</span>
      </div>
      {!readOnly && onRemove && (
        <div className="flex items-start pt-2 pr-2">
          <button
            type="button"
            aria-label={`Remove ${name}`}
            onClick={onRemove}
            className="icon-action cursor-pointer"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
